import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import type {
  AuthConfigResponse,
  AuthConfigUpdateRequest,
  ExternalAuthProvider,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

export function AdminAuth() {
  const cfg = useQuery({
    queryKey: ['admin', 'auth-config'],
    queryFn: () => api.adminAuthConfig(),
  });

  return (
    <PageShell
      header={
        <PageHeader
          title="인증 설정"
          description="관리자 IP 제한 / 외부 인증 provider / 세션 강제 로그아웃. 잘못 설정되어 잠겨도 CLI admin:reset-password 로 복구할 수 있습니다."
        />
      }
    >
      {cfg.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : cfg.error || !cfg.data ? (
        <p className="text-sm text-destructive">
          설정을 불러올 수 없습니다:{' '}
          {cfg.error instanceof ApiError ? cfg.error.message : '알 수 없는 오류'}
        </p>
      ) : (
        <div className="mx-auto max-w-3xl space-y-6">
          <IpAllowlistCard data={cfg.data} />
          <ExternalProviderCard data={cfg.data} />
          <MicrosoftCard data={cfg.data} />
          <LdapCard data={cfg.data} />
          <IsignCard data={cfg.data} />
          <ForceLogoutCard />
        </div>
      )}
    </PageShell>
  );
}

function IpAllowlistCard({ data }: { data: AuthConfigResponse }) {
  const qc = useQueryClient();
  const [text, setText] = useState(data.localAdminIpAllowlist.join('\n'));
  const [pagesRestricted, setPagesRestricted] = useState(data.adminPagesIpRestricted);
  const [acceptRisk, setAcceptRisk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setText(data.localAdminIpAllowlist.join('\n'));
    setPagesRestricted(data.adminPagesIpRestricted);
  }, [data]);

  const mut = useMutation({
    mutationFn: (body: AuthConfigUpdateRequest) => api.adminAuthConfigUpdate(body),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      setAcceptRisk(false);
      qc.invalidateQueries({ queryKey: ['admin', 'auth-config'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : '저장 실패');
      }
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const list = text
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (list.length === 0) {
      setError('allowlist 가 비어있습니다. 최소 1개 CIDR 필요 (전부 허용은 0.0.0.0/0).');
      return;
    }
    mut.mutate({
      localAdminIpAllowlist: list,
      adminPagesIpRestricted: pagesRestricted,
      acceptSelfLockoutRisk: acceptRisk || undefined,
    });
  };

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">관리자 IP allowlist</h2>
        <p className="text-sm text-foreground-muted mt-1">
          관리자 로컬 로그인 (이메일/비밀번호) 은 항상 이 allowlist 로 검사됩니다.{' '}
          <code className="px-1 py-0.5 bg-background border border-border rounded text-xs">
            0.0.0.0/0
          </code>{' '}
          이면 전부 허용. 비워두면 락아웃되니 주의.
        </p>
        <p className="text-xs text-foreground-muted mt-1">
          현재 IP: <code className="font-mono">{data.currentIp}</code>
        </p>
      </header>
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium">CIDR (줄당 1개)</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            spellCheck={false}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
            placeholder="0.0.0.0/0&#10;10.0.0.0/8"
          />
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={pagesRestricted}
            onChange={(e) => setPagesRestricted(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            관리자 페이지 (<code className="text-xs">/admin/*</code>) 도 같은 allowlist 로 제한.
            기본은 OFF — 켤 경우 사용자 화면에서 admin 으로 이동도 IP 검사.
          </span>
        </label>
        {error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive space-y-2">
            <div>{error}</div>
            {error.includes('acceptSelfLockoutRisk') ? (
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={acceptRisk}
                  onChange={(e) => setAcceptRisk(e.target.checked)}
                />
                <span>자기 차단 위험을 이해했고 그래도 저장. (CLI reset 으로 복구 가능)</span>
              </label>
            ) : null}
          </div>
        ) : null}
        {savedAt ? <div className="text-sm text-foreground-muted">저장됨</div> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function ExternalProviderCard({ data }: { data: AuthConfigResponse }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<ExternalAuthProvider | null>(
    data.activeExternalProvider,
  );
  const [autoRedirect, setAutoRedirect] = useState<boolean>(data.ssoAutoRedirect);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setSelected(data.activeExternalProvider);
    setAutoRedirect(data.ssoAutoRedirect);
  }, [data]);

  const dirty = selected !== data.activeExternalProvider || autoRedirect !== data.ssoAutoRedirect;

  const mut = useMutation({
    mutationFn: (body: AuthConfigUpdateRequest) => api.adminAuthConfigUpdate(body),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['admin', 'auth-config'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '저장 실패');
    },
  });

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">외부 인증 provider</h2>
        <p className="text-sm text-foreground-muted mt-1">
          한 번에 1개만 활성. 활성 시 일반 사용자는 외부 provider 로만 로그인 가능 (admin 은 로컬
          로그인도 살아있음). 미설정 provider 는 활성화 거부됩니다.
        </p>
      </header>
      <div className="space-y-2 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="ext-provider"
            checked={selected === null}
            onChange={() => setSelected(null)}
          />
          <span>사용 안 함 (로컬 ID/PW 만)</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="ext-provider"
            checked={selected === 'microsoft'}
            onChange={() => setSelected('microsoft')}
            disabled={!data.microsoft}
          />
          <span>
            Microsoft Entra
            {!data.microsoft ? (
              <span className="ml-2 text-xs text-foreground-muted">(먼저 아래 폼에서 등록)</span>
            ) : null}
          </span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="ext-provider"
            checked={selected === 'ldap'}
            onChange={() => setSelected('ldap')}
            disabled={!data.ldap}
          />
          <span>
            LDAP
            {!data.ldap ? (
              <span className="ml-2 text-xs text-foreground-muted">(먼저 아래 폼에서 등록)</span>
            ) : null}
          </span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="ext-provider"
            checked={selected === 'isign'}
            onChange={() => setSelected('isign')}
            disabled={!data.isign}
          />
          <span>
            Pentasecurity ISign+
            {!data.isign ? (
              <span className="ml-2 text-xs text-foreground-muted">(먼저 아래 폼에서 등록)</span>
            ) : null}
          </span>
        </label>

        <div className="pt-3 mt-3 border-t border-border">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={autoRedirect}
              onChange={(e) => setAutoRedirect(e.target.checked)}
              disabled={selected === null}
              className="mt-0.5"
            />
            <span>
              로그인 페이지 진입 시 SSO 로 자동 이동
              <span className="block text-xs text-foreground-muted mt-0.5">
                활성 시 사용자가 <code>/login</code> 접속 즉시{' '}
                <code>/api/auth/&lt;provider&gt;/start</code> 로 navigate. 비상시{' '}
                <code>/login/admin</code> 으로 로컬 로그인 가능.
              </span>
            </span>
          </label>
        </div>
      </div>
      {error ? (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {savedAt ? <div className="mt-3 text-sm text-foreground-muted">저장됨</div> : null}
      <div className="mt-4 flex justify-end">
        <Button
          type="button"
          disabled={mut.isPending || !dirty}
          onClick={() =>
            mut.mutate({ activeExternalProvider: selected, ssoAutoRedirect: autoRedirect })
          }
        >
          {mut.isPending ? '저장 중…' : '적용'}
        </Button>
      </div>
    </section>
  );
}

function MicrosoftCard({ data }: { data: AuthConfigResponse }) {
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState(data.microsoft?.tenantId ?? '');
  const [clientId, setClientId] = useState(data.microsoft?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setTenantId(data.microsoft?.tenantId ?? '');
    setClientId(data.microsoft?.clientId ?? '');
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () =>
      api.adminAuthMicrosoftUpdate({
        tenantId,
        clientId,
        clientSecret: clientSecret || undefined,
      }),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      setClientSecret('');
      qc.invalidateQueries({ queryKey: ['admin', 'auth-config'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '저장 실패');
    },
  });

  const testMut = useMutation({
    mutationFn: () =>
      api.adminAuthMicrosoftTest({
        tenantId,
        clientId,
        clientSecret: clientSecret || undefined,
      }),
    onSuccess: (res) => setTestResult({ ok: res.ok, message: res.message }),
    onError: (err: unknown) =>
      setTestResult({ ok: false, message: err instanceof ApiError ? err.message : '테스트 실패' }),
  });

  const noChange =
    tenantId === (data.microsoft?.tenantId ?? '') &&
    clientId === (data.microsoft?.clientId ?? '') &&
    !clientSecret;

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">Microsoft Entra 등록</h2>
        <p className="text-sm text-foreground-muted mt-1">
          Azure 포털에서 앱을 등록하고 redirect URI 에 다음을 추가:{' '}
          <code className="px-1 py-0.5 bg-background border border-border rounded text-xs">
            {window.location.origin}/api/auth/microsoft/callback
          </code>
        </p>
        <p className="text-xs text-foreground-muted mt-1">
          저장과 활성화는 별개. 저장 후 위쪽 "외부 인증 provider" 에서 Microsoft 를 활성화하세요.
        </p>
        {data.activeExternalProvider === 'microsoft' ? (
          <p className="text-xs text-foreground-muted mt-2">
            테스트 로그인:{' '}
            <a
              href="/api/auth/microsoft/start"
              target="_blank"
              rel="noreferrer noopener"
              className="underline hover:opacity-80"
            >
              {window.location.origin}/api/auth/microsoft/start
            </a>{' '}
            (새 탭에서 열림)
          </p>
        ) : null}
      </header>
      <div className="space-y-3">
        <Field label="Tenant ID (Directory ID)" htmlFor="ms-tenant">
          <Input
            id="ms-tenant"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            spellCheck={false}
          />
        </Field>
        <Field label="Client ID (Application ID)" htmlFor="ms-client">
          <Input
            id="ms-client"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            spellCheck={false}
          />
        </Field>
        <Field label="Client Secret (Value, Secret ID 아님)" htmlFor="ms-secret">
          <Input
            id="ms-secret"
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={
              data.microsoft?.hasSecret ? '비워두면 기존 secret 유지' : '신규 secret 입력 필수'
            }
          />
          <p className="mt-1 text-xs text-foreground-muted">
            Azure Portal → Certificates & secrets 에서 secret 을 만들면 두 컬럼이 보이는데,{' '}
            <strong>Value</strong> (긴 문자열) 를 복사하세요. <strong>Secret ID</strong> (UUID 형태)
            는 다른 값입니다.
          </p>
        </Field>
      </div>
      {testResult ? (
        <div
          className={
            testResult.ok
              ? 'mt-3 rounded-md border border-[hsl(140_60%_45%)]/30 bg-[hsl(140_60%_45%)]/5 px-3 py-2 text-sm text-[hsl(140_60%_30%)]'
              : 'mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'
          }
        >
          {testResult.message}
        </div>
      ) : null}
      {error ? (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {savedAt ? <div className="mt-3 text-sm text-foreground-muted">저장됨</div> : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={
            testMut.isPending ||
            !tenantId ||
            !clientId ||
            (!data.microsoft?.hasSecret && !clientSecret)
          }
          onClick={() => testMut.mutate()}
        >
          {testMut.isPending ? '테스트 중…' : '연결 테스트'}
        </Button>
        <Button
          type="button"
          disabled={
            saveMut.isPending ||
            noChange ||
            !tenantId ||
            !clientId ||
            (!data.microsoft?.hasSecret && !clientSecret)
          }
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? '저장 중…' : '저장'}
        </Button>
      </div>
    </section>
  );
}

function LdapCard({ data }: { data: AuthConfigResponse }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(data.ldap?.url ?? '');
  const [baseDN, setBaseDN] = useState(data.ldap?.baseDN ?? '');
  const [bindDN, setBindDN] = useState(data.ldap?.bindDN ?? '');
  const [bindCredential, setBindCredential] = useState('');
  const [userSearchFilter, setUserSearchFilter] = useState(
    data.ldap?.userSearchFilter ?? '(mail={username})',
  );
  const [emailAttribute, setEmailAttribute] = useState(data.ldap?.emailAttribute ?? 'mail');
  const [displayNameAttribute, setDisplayNameAttribute] = useState(
    data.ldap?.displayNameAttribute ?? 'displayName',
  );
  const [startTLS, setStartTLS] = useState(data.ldap?.startTLS ?? false);
  const [bindMode, setBindMode] = useState<'search' | 'direct'>(data.ldap?.bindMode ?? 'search');
  const [directBindFormat, setDirectBindFormat] = useState(data.ldap?.directBindFormat ?? '');
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data.ldap) {
      setUrl(data.ldap.url);
      setBaseDN(data.ldap.baseDN);
      setBindDN(data.ldap.bindDN);
      setUserSearchFilter(data.ldap.userSearchFilter);
      setEmailAttribute(data.ldap.emailAttribute);
      setDisplayNameAttribute(data.ldap.displayNameAttribute);
      setStartTLS(data.ldap.startTLS);
      setBindMode(data.ldap.bindMode);
      setDirectBindFormat(data.ldap.directBindFormat);
    }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () =>
      api.adminAuthLdapUpdate({
        url,
        baseDN,
        bindDN,
        bindCredential: bindCredential || undefined,
        userSearchFilter,
        emailAttribute,
        displayNameAttribute,
        startTLS,
        bindMode,
        directBindFormat: directBindFormat || undefined,
      }),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      setBindCredential('');
      qc.invalidateQueries({ queryKey: ['admin', 'auth-config'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '저장 실패');
    },
  });

  const testMut = useMutation({
    mutationFn: () =>
      api.adminAuthLdapTest({
        url,
        baseDN,
        bindDN,
        bindCredential: bindCredential || undefined,
        startTLS,
      }),
    onSuccess: (res) => setTestResult({ ok: res.ok, message: res.message }),
    onError: (err: unknown) =>
      setTestResult({ ok: false, message: err instanceof ApiError ? err.message : '테스트 실패' }),
  });

  // [임시 — service account 미발급 환경 대응] bindDN/credential 필수 강제 해제.
  // direct 모드 추가로 bindMode 별 분기 검증.
  // 원복 시: 아래 relaxed 라인 제거 + 그 위 strict 두 라인 주석 해제.
  // const requireCredentialFresh = !data.ldap?.hasBindCredential && !bindCredential;
  // const formInvalid = !url || !baseDN || !bindDN || !userSearchFilter || requireCredentialFresh;
  const formInvalid =
    !url ||
    (bindMode === 'search'
      ? !baseDN || !userSearchFilter
      : !directBindFormat || !directBindFormat.includes('{username}'));

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">LDAP 등록</h2>
        <p className="text-sm text-foreground-muted mt-1">
          저장과 활성화는 별개. 저장 후 위쪽 "외부 인증 provider" 에서 LDAP 를 활성화하세요.
        </p>
        <ul className="text-xs text-foreground-muted mt-1 list-disc ml-4 space-y-0.5">
          <li>
            <strong>Search</strong>: service account 로 bind → userSearchFilter 로 사용자 검색 →
            사용자 비번 re-bind. 일반적인 OpenLDAP / FreeIPA 환경.
          </li>
          <li>
            <strong>Direct</strong>: search 없이 사용자 ID 로 바로 bind. AD 의 NetBIOS prefix (
            <code>{'<도메인>\\\\{username}'}</code>) / UPN (<code>{'{username}@<도메인>'}</code>)
            형식. 사내 도메인으로 치환해 입력.
          </li>
        </ul>
      </header>
      <div className="space-y-3">
        <Field label="URL (ldap:// 또는 ldaps://)" htmlFor="ldap-url">
          <Input
            id="ldap-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="ldaps://ldap.example.com:636"
            spellCheck={false}
          />
        </Field>
        <div>
          <span className="text-sm font-medium">Bind 모드</span>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="ldap-bind-mode"
                value="search"
                checked={bindMode === 'search'}
                onChange={() => setBindMode('search')}
              />
              <span>Search (service account)</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="ldap-bind-mode"
                value="direct"
                checked={bindMode === 'direct'}
                onChange={() => setBindMode('direct')}
              />
              <span>Direct (사용자 ID 로 바로 bind)</span>
            </label>
          </div>
        </div>
        {bindMode === 'search' ? (
          <>
            <Field label="Base DN" htmlFor="ldap-base">
              <Input
                id="ldap-base"
                value={baseDN}
                onChange={(e) => setBaseDN(e.target.value)}
                placeholder="dc=example,dc=com"
                spellCheck={false}
              />
            </Field>
            <Field label="Bind DN (서비스 계정)" htmlFor="ldap-bind">
              <Input
                id="ldap-bind"
                value={bindDN}
                onChange={(e) => setBindDN(e.target.value)}
                placeholder="cn=service,dc=example,dc=com (anonymous 허용 LDAP 면 비워도 됨)"
                spellCheck={false}
              />
            </Field>
            <Field label="Bind 비밀번호" htmlFor="ldap-bind-pw">
              <Input
                id="ldap-bind-pw"
                type="password"
                value={bindCredential}
                onChange={(e) => setBindCredential(e.target.value)}
                placeholder={
                  data.ldap?.hasBindCredential
                    ? '비워두면 기존 비밀번호 유지'
                    : '신규 비밀번호 (anonymous 면 빈 값)'
                }
              />
            </Field>
            <Field label="User Search Filter" htmlFor="ldap-filter">
              <Input
                id="ldap-filter"
                value={userSearchFilter}
                onChange={(e) => setUserSearchFilter(e.target.value)}
                placeholder="(mail={username})"
                spellCheck={false}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="email attribute" htmlFor="ldap-email-attr">
                <Input
                  id="ldap-email-attr"
                  value={emailAttribute}
                  onChange={(e) => setEmailAttribute(e.target.value)}
                  placeholder="mail"
                  spellCheck={false}
                />
              </Field>
              <Field label="displayName attribute" htmlFor="ldap-name-attr">
                <Input
                  id="ldap-name-attr"
                  value={displayNameAttribute}
                  onChange={(e) => setDisplayNameAttribute(e.target.value)}
                  placeholder="displayName"
                  spellCheck={false}
                />
              </Field>
            </div>
          </>
        ) : (
          <>
            <Field label="Direct Bind Format" htmlFor="ldap-direct-format">
              <Input
                id="ldap-direct-format"
                value={directBindFormat}
                onChange={(e) => setDirectBindFormat(e.target.value)}
                placeholder="<도메인>\\{username}  또는  {username}@<도메인>"
                spellCheck={false}
              />
            </Field>
            <p className="text-xs text-foreground-muted">
              로그인 폼의 입력값이 <code>{'{username}'}</code> 자리에 그대로 치환되어 LDAP 에 bind
              됩니다. AD 환경에서 NetBIOS 도메인 prefix 또는 UPN 형식 사용. direct 모드는 LDAP 에서
              추가 정보를 가져오지 않으므로 사용자 email/displayName 은 로그인 시 입력값을 그대로
              사용합니다.
            </p>
          </>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={startTLS}
            onChange={(e) => setStartTLS(e.target.checked)}
          />
          <span>StartTLS 사용 (ldap:// 비암호화 연결을 TLS 로 업그레이드)</span>
        </label>
      </div>
      {testResult ? (
        <div
          className={
            testResult.ok
              ? 'mt-3 rounded-md border border-[hsl(140_60%_45%)]/30 bg-[hsl(140_60%_45%)]/5 px-3 py-2 text-sm text-[hsl(140_60%_30%)]'
              : 'mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'
          }
        >
          {testResult.message}
        </div>
      ) : null}
      {error ? (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {savedAt ? <div className="mt-3 text-sm text-foreground-muted">저장됨</div> : null}
      <div className="mt-4 flex justify-end gap-2">
        {/* [임시 — service account 미발급 환경 대응] 연결 테스트 비활성화. URL 만으로 LDAP 등록/로그인 시도 허용. */}
        {/* 원복 시: 아래 disabled-block 제거 + 그 아래 active-block 주석 해제. */}
        <Button
          type="button"
          variant="outline"
          disabled
          title="현재 비활성화 (service account 없이 등록 허용 모드)"
          onClick={() => testMut.mutate()}
        >
          연결 테스트 (임시 비활성)
        </Button>
        {/*
        <Button
          type="button"
          variant="outline"
          disabled={testMut.isPending || !url || !baseDN || !bindDN || requireCredentialFresh}
          onClick={() => testMut.mutate()}
        >
          {testMut.isPending ? '테스트 중…' : '연결 테스트'}
        </Button>
        */}
        <Button
          type="button"
          disabled={saveMut.isPending || formInvalid}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? '저장 중…' : '저장'}
        </Button>
      </div>
    </section>
  );
}

function IsignCard({ data }: { data: AuthConfigResponse }) {
  const qc = useQueryClient();
  const isign = data.isign;
  const [serverUrl, setServerUrl] = useState(isign?.serverUrl ?? '');
  const [agentId, setAgentId] = useState(isign?.agentId ?? '');
  const [useOauth, setUseOauth] = useState(isign?.useOauth ?? '0');
  const [requestData, setRequestData] = useState(isign?.requestData ?? 'id,sAMAccountName');
  const [tokenAuthorizationPath, setTokenAuthorizationPath] = useState(
    isign?.tokenAuthorizationPath ?? '/token/authorization',
  );
  const [loginPath, setLoginPath] = useState(isign?.loginPath ?? '/login.html');
  const [logoutPath, setLogoutPath] = useState(isign?.logoutPath ?? '/logout.html');
  const [checkServerPath, setCheckServerPath] = useState(
    isign?.checkServerPath ?? '/openapi/checkserver',
  );
  const [saveTokenPath, setSaveTokenPath] = useState(
    isign?.saveTokenPath ?? '/token/saveToken.html',
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  // data 가 바뀌면 (저장 후 invalidate) 폼 동기화.
  useEffect(() => {
    setServerUrl(data.isign?.serverUrl ?? '');
    setAgentId(data.isign?.agentId ?? '');
    setUseOauth(data.isign?.useOauth ?? '0');
    setRequestData(data.isign?.requestData ?? 'id,sAMAccountName');
    setTokenAuthorizationPath(data.isign?.tokenAuthorizationPath ?? '/token/authorization');
    setLoginPath(data.isign?.loginPath ?? '/login.html');
    setLogoutPath(data.isign?.logoutPath ?? '/logout.html');
    setCheckServerPath(data.isign?.checkServerPath ?? '/openapi/checkserver');
    setSaveTokenPath(data.isign?.saveTokenPath ?? '/token/saveToken.html');
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () =>
      api.adminAuthIsignUpdate({
        serverUrl,
        agentId,
        useOauth,
        requestData,
        tokenAuthorizationPath,
        loginPath,
        logoutPath,
        checkServerPath,
        saveTokenPath,
      }),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['admin', 'auth-config'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '저장 실패');
    },
  });

  const testMut = useMutation({
    mutationFn: () => api.adminAuthIsignTest({ serverUrl, agentId, checkServerPath }),
    onSuccess: (res) => {
      setTestResult(res);
    },
    onError: (err: unknown) => {
      setTestResult({ ok: false, message: err instanceof ApiError ? err.message : '테스트 실패' });
    },
  });

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">ISign+ 등록</h2>
        <p className="text-sm text-foreground-muted mt-1">
          저장과 활성화는 별개. 저장 후 위쪽 "외부 인증 provider" 에서 ISign+ 를 활성화하세요.
        </p>
        <p className="text-xs text-foreground-muted mt-1">
          ISign+ 운영 콘솔에 두 URL 을 등록하세요:
          <br />· 검증 URL: <code>{window.location.origin}/checkauth</code> (또는{' '}
          <code>{window.location.origin}/api/auth/isign/callback</code>)
          <br />· 인증 성공 URL: <code>{window.location.origin}/agentProc</code>
        </p>
      </header>

      <div className="space-y-3 text-sm">
        <div>
          <label className="block mb-1 text-foreground-muted">ISign+ 서버 URL</label>
          <input
            type="text"
            className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
            value={serverUrl}
            onChange={(e) => setServerUrl(e.target.value)}
            placeholder="https://sso.example.com"
          />
        </div>
        <div>
          <label className="block mb-1 text-foreground-muted">Agent ID</label>
          <input
            type="text"
            className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            placeholder="19"
          />
        </div>
        <div>
          <label className="block mb-1 text-foreground-muted">useOauth</label>
          <input
            type="text"
            className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
            value={useOauth}
            onChange={(e) => setUseOauth(e.target.value)}
            placeholder="0"
          />
          <p className="mt-1 text-xs text-foreground-muted">
            ISign+ OAuth 모드 플래그. 기본 <code>0</code>. 로그인 URL 의 <code>useOauth</code>{' '}
            쿼리로 전달.
          </p>
        </div>
        <div>
          <label className="block mb-1 text-foreground-muted">사용자 속성</label>
          <input
            type="text"
            className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
            value={requestData}
            onChange={(e) => setRequestData(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="text-xs text-foreground-muted hover:text-foreground underline"
          onClick={() => setAdvancedOpen((v) => !v)}
        >
          {advancedOpen ? '▾' : '▸'} 경로 설정
        </button>

        {advancedOpen ? (
          <div className="space-y-2 pl-3 border-l border-border">
            {(
              [
                ['토큰 검증', tokenAuthorizationPath, setTokenAuthorizationPath],
                ['로그인 페이지', loginPath, setLoginPath],
                ['로그아웃', logoutPath, setLogoutPath],
                ['헬스체크', checkServerPath, setCheckServerPath],
                ['토큰 저장', saveTokenPath, setSaveTokenPath],
              ] as const
            ).map(([label, value, setter]) => (
              <div key={label}>
                <label className="block mb-1 text-xs text-foreground-muted">{label}</label>
                <input
                  type="text"
                  className="w-full bg-background border border-border rounded px-3 py-1.5 text-xs"
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                />
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {savedAt ? <div className="mt-3 text-sm text-foreground-muted">저장됨</div> : null}
      {testResult ? (
        <div
          className={`mt-3 rounded-md px-3 py-2 text-sm ${testResult.ok ? 'border border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400' : 'border border-destructive/30 bg-destructive/5 text-destructive'}`}
        >
          {testResult.message}
        </div>
      ) : null}

      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={testMut.isPending || !serverUrl || !agentId}
          onClick={() => testMut.mutate()}
        >
          {testMut.isPending ? '확인 중…' : '연결 테스트'}
        </Button>
        <Button
          type="button"
          disabled={saveMut.isPending || !serverUrl || !agentId}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? '저장 중…' : '저장'}
        </Button>
      </div>

      {data.activeExternalProvider === 'isign' ? (
        <p className="mt-4 text-xs text-foreground-muted">
          시작: <code>{window.location.origin}/api/auth/isign/start</code>
        </p>
      ) : null}
    </section>
  );
}

function ForceLogoutCard() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const mut = useMutation({
    mutationFn: () => api.adminAuthForceLogout(),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['admin', 'auth-config'] });
    },
    onError: (err: unknown) => setError(err instanceof ApiError ? err.message : '실행 실패'),
  });

  const onClick = () => {
    if (
      confirm(
        '모든 활성 세션을 강제 로그아웃합니다. 본인도 로그아웃되며 다시 로그인이 필요합니다. 계속할까요?',
      )
    ) {
      mut.mutate();
    }
  };

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">모든 세션 강제 로그아웃</h2>
        <p className="text-sm text-foreground-muted mt-1">
          외부 provider 전환 / 보안 이슈 의심 시 사용. session_epoch 을 현재 시각으로 bump 해서 그
          이전 발급된 모든 JWT 를 거부합니다.
        </p>
      </header>
      {error ? (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {savedAt ? <div className="mt-3 text-sm text-foreground-muted">실행 완료</div> : null}
      <div className="mt-4 flex justify-end">
        <Button type="button" variant="outline" disabled={mut.isPending} onClick={onClick}>
          {mut.isPending ? '실행 중…' : '모두 강제 로그아웃'}
        </Button>
      </div>
    </section>
  );
}
